import { Dispatch, RefObject, useEffect, useRef } from "react";
import {
  DatePickerMenuAlignment,
  DatePickerMenuPosition,
  DatePickerSize,
  DatePickerTrigger,
} from "./datePicker.types";
import { DatePickerAction } from "./useDatePickerState";

const DEFAULT_POPUP_MIN_WIDTH_PX = 360;
const COMPACT_POPUP_MIN_WIDTH_PX = 320;
const EDGE_GAP_PX = 8;
const VERTICAL_OFFSET_PX = 8;

interface UseDatePickerPositioningParams {
  readonly buttonTriggerRef: RefObject<HTMLDivElement>;
  readonly dispatch: Dispatch<DatePickerAction>;
  readonly isOpen: boolean;
  readonly menuAlignment?: DatePickerMenuAlignment;
  readonly popupRef: RefObject<HTMLDivElement>;
  readonly size: DatePickerSize;
  readonly trigger: DatePickerTrigger;
  readonly triggerRef: RefObject<HTMLInputElement>;
}

interface DatePickerPositioningParams {
  readonly anchorRect: DOMRect;
  readonly menuAlignment?: DatePickerMenuAlignment;
  readonly popupHeight: number;
  readonly size: DatePickerSize;
  readonly viewportHeight: number;
  readonly viewportWidth: number;
}

interface DatePickerAnchorParams {
  readonly buttonTriggerRef: RefObject<HTMLDivElement>;
  readonly trigger: DatePickerTrigger;
  readonly triggerRef: RefObject<HTMLInputElement>;
}

export function useDatePickerPositioning({
  buttonTriggerRef,
  dispatch,
  isOpen,
  menuAlignment,
  popupRef,
  size,
  trigger,
  triggerRef,
}: UseDatePickerPositioningParams): void {
  const lastMenuPositionRef = useRef<DatePickerMenuPosition | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = (): void => {
      const anchorElement = getAnchorElement({
        buttonTriggerRef,
        trigger,
        triggerRef,
      });
      const rect = anchorElement?.getBoundingClientRect();
      if (!rect) return;
      const nextPosition = getMenuPosition({
        anchorRect: rect,
        menuAlignment,
        popupHeight: popupRef.current?.offsetHeight ?? 0,
        size,
        viewportHeight: window.innerHeight,
        viewportWidth: window.innerWidth,
      });
      if (isSamePosition(lastMenuPositionRef.current, nextPosition)) return;
      lastMenuPositionRef.current = nextPosition;
      dispatch({
        type: "set-menu-position",
        menuPosition: nextPosition,
      });
    };

    updatePosition();
    requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [
    buttonTriggerRef,
    dispatch,
    isOpen,
    menuAlignment,
    popupRef,
    size,
    trigger,
    triggerRef,
  ]);
}

function getAnchorElement(params: DatePickerAnchorParams): HTMLElement | null {
  const triggerElement = params.triggerRef.current;
  const activeTriggerElement =
    params.trigger === "button"
      ? params.buttonTriggerRef.current
      : triggerElement;
  return activeTriggerElement?.parentElement ?? activeTriggerElement;
}

function getMenuPosition(
  params: DatePickerPositioningParams,
): DatePickerMenuPosition {
  const width = getMenuWidth(params);
  return {
    left: getMenuLeft(params, width),
    top: getMenuTop(params),
    width,
  };
}

function getMenuWidth(params: DatePickerPositioningParams): number {
  const minWidth =
    params.size === "compact"
      ? COMPACT_POPUP_MIN_WIDTH_PX
      : DEFAULT_POPUP_MIN_WIDTH_PX;
  const maxWidth = Math.max(240, params.viewportWidth - EDGE_GAP_PX * 2);
  return Math.min(Math.max(params.anchorRect.width, minWidth), maxWidth);
}

function getMenuTop(params: DatePickerPositioningParams): number {
  const topBelow = params.anchorRect.bottom + VERTICAL_OFFSET_PX;
  const topAbove =
    params.anchorRect.top - params.popupHeight - VERTICAL_OFFSET_PX;
  const shouldPlaceBelow =
    topBelow + params.popupHeight <= params.viewportHeight - EDGE_GAP_PX ||
    topAbove < EDGE_GAP_PX;
  if (!shouldPlaceBelow) return Math.max(EDGE_GAP_PX, topAbove);
  return Math.min(
    Math.max(EDGE_GAP_PX, topBelow),
    Math.max(
      EDGE_GAP_PX,
      params.viewportHeight - params.popupHeight - EDGE_GAP_PX,
    ),
  );
}

function getMenuLeft(
  params: DatePickerPositioningParams,
  width: number,
): number {
  const desiredLeft =
    params.menuAlignment === "right"
      ? params.anchorRect.right - width
      : params.anchorRect.left;
  return Math.min(
    Math.max(EDGE_GAP_PX, desiredLeft),
    Math.max(EDGE_GAP_PX, params.viewportWidth - width - EDGE_GAP_PX),
  );
}

function isSamePosition(
  previousPosition: DatePickerMenuPosition | null,
  nextPosition: DatePickerMenuPosition,
): boolean {
  return (
    previousPosition != null &&
    previousPosition.top === nextPosition.top &&
    previousPosition.left === nextPosition.left &&
    previousPosition.width === nextPosition.width
  );
}
